import { IFinancialProductSponsor } from '@/types/financial-product-sponsor'
import { IFinancialTerm } from '@/types/financial-term'
import { Tooltip, Typography } from '@mui/material'
import { useClickAway } from '@uidotdev/usehooks'
import { countries } from 'countries-list'
import { useTranslations } from 'next-intl'
import { ComponentProps, ReactNode, useState } from 'react'
import { ISAChipWithSponsor } from '../../../checkout/components/ISAChipWithSponsor'

type ISAPaymentOptionAdProps = ComponentProps<'div'> & {
  financialTerm?: IFinancialTerm
  children?: ReactNode
  variant?: 'default' | 'compact'
}

export function ISAPaymentOptionAd({
  financialTerm,
  children,
  variant = 'default',
  ...props
}: ISAPaymentOptionAdProps) {
  const { availableCountries } = financialTerm ?? {}
  const financialProductSponsor =
    financialTerm?.financialProductSponsor as IFinancialProductSponsor
  const t = useTranslations('ISAPaymentOptionAd')
  const [isOpen, setIsOpen] = useState(false)

  const tooltipRef = useClickAway(() => {
    setIsOpen(false)
  })

  if (!financialProductSponsor) {
    return <>{children}</>
  }

  const countryNames =
    availableCountries
      ?.map((countryCode) => countries[countryCode?.toUpperCase()]?.name)
      .join(', ') || t('selected_countries')

  return (
    <div {...props} className={`flex flex-col gap-4 ${props.className ?? ''}`}>
      <div className="flex flex-col">
        <div className="flex flex-wrap items-baseline justify-between gap-1">
          <div className="flex flex-col">
            <Typography variant="subtitle2" color="text.primary" className="">
              {t('learn_today_pay_later')}
            </Typography>
            <Typography variant="caption" className="text-xxs text-[#000]">
              {t('via_income_share_agreement')}
            </Typography>
          </div>

          <ISAChipWithSponsor
            availableCountries={availableCountries}
            financialProductSponsor={financialProductSponsor}
            showCountryFlags
          />
        </div>

        {variant === 'default' && (
          <Tooltip
            title={t('isa_description')}
            className="self-end"
            arrow
            placement="top-end"
            open={isOpen}
            onClose={() => {
              setIsOpen(false)
            }}
            disableHoverListener
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'white',
                  color: '#333',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  fontSize: '14px',
                  padding: '12px 16px',
                  maxWidth: '300px',
                },
              },
            }}
          >
            <div ref={tooltipRef as any}>
              <Typography
                variant="caption"
                className="cursor-pointer self-end text-right underline"
                onClick={() => setIsOpen(!isOpen)}
              >
                {t('what_is_an_isa')}
              </Typography>
            </div>
          </Tooltip>
        )}
      </div>

      {variant === 'default' && children}

      <Typography variant="caption" className="text-xxs text-[#000]">
        {t('to_choose_the_isa_powered_by_sponsor_option', {
          sponsorName: financialProductSponsor.name,
          countryNames,
        })}
      </Typography>
    </div>
  )
}
